import React, { useEffect, useRef, useState } from 'react'
import { Form, Select, Typography } from 'antd'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import { Modal } from 'src/components/Ant'
import {
  Avatar,
  Request,
  RequestTicket,
  RequestTicketUserData
} from 'src/types'
import {
  resetSaveFullRequest,
  saveRequestTickets
} from 'src/core/store/ducks/requests/actions'
import { useT } from 'src/core/hooks/use-translation'
import {
  getInitialValues,
  getValidationSchema
} from 'src/core/forms/request/advance'
import useStandardResource from 'src/core/hooks/use-standard-resource'

import FormFields from '../FormFields'

import { WrapAlert } from './styles'

type Props = {
  itemEdit?: RequestTicket
  request: Request
  toggle(): void
  visible: boolean
}

function newUserData(traveler: Avatar): RequestTicketUserData {
  return {
    included: true,
    owner: true,
    request_ticket_id: null,
    request_ticket_user_data_id: null,
    traveler: {
      gender: traveler.gender,
      name: traveler.name,
      user_id: traveler.user_id
    },
    user_id: traveler.user_id
  }
}
const initialValues = getInitialValues()

function ModalForm({ itemEdit, toggle, visible }: Props) {
  const dispatch = useDispatch()
  const { requests } = useSelector((state: ApplicationState) => ({
    requests: state.requests
  }))

  const [saving, setSaving] = useState<boolean>(false)

  const refReceiverIdSelect = useRef<Select<string>>(null)
  const refFormik = useRef<Formik<RequestTicket>>(null)

  const currencyStandard = useStandardResource('currencies')
  const financialResourceStandard = useStandardResource('financial_resources')

  const { t } = useT()

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        refFormik.current.resetForm(
          !itemEdit
            ? {
                ...initialValues,
                request_ticket_title_advances: [
                  {
                    ...initialValues.request_ticket_title_advances[0],
                    currency: currencyStandard,
                    currency_id: currencyStandard
                      ? currencyStandard.currency_id.toString()
                      : null,
                    financial_resource: financialResourceStandard,
                    financial_resource_id: financialResourceStandard
                      ? financialResourceStandard.financial_resource_id.toString()
                      : null
                  }
                ]
              }
            : {
                ...itemEdit,
                receiver_id:
                  itemEdit.request_ticket_user_data
                    .find(({ owner }) => owner)
                    ?.traveler?.user_id?.toString() || null,
                request_ticket_title_advances: [
                  {
                    ...itemEdit.request_ticket_title_advances[0],
                    currency_id:
                      itemEdit.request_ticket_title_advances[0].currency_id.toString(),
                    financial_resource_id:
                      itemEdit.request_ticket_title_advances[0].financial_resource_id.toString()
                  }
                ]
              }
        )
        refReceiverIdSelect.current.focus()
      }, 100)
    }
  }, [itemEdit, currencyStandard, financialResourceStandard, visible])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== saving) {
      setSaving(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading && !requests.saveFullRequestError) {
        toggle()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  return (
    <Modal
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      forceRender
      maskClosable={!saving}
      okButtonProps={{ loading: saving }}
      onCancel={() => {
        toggle()
        dispatch(resetSaveFullRequest())
      }}
      onOk={() => refFormik.current.submitForm()}
      title={
        <>
          {itemEdit
            ? t('web.services.advances.form.title-edit')
            : t('web.services.advances.form.title')}
          {!!itemEdit && (
            <Typography.Text code>
              #{itemEdit.request_ticket_id}
            </Typography.Text>
          )}
        </>
      }
      visible={visible}
      width={820}
    >
      <Formik
        ref={refFormik}
        initialValues={initialValues}
        onSubmit={values => {
          const request_ticket_user_data: RequestTicketUserData[] = []
          if (!values.request_ticket_id) {
            if (values.receiver_id === '*') {
              requests.fullRequest.request_user_data.forEach(({ traveler }) => {
                request_ticket_user_data.push(newUserData(traveler))
              })
            } else {
              const receiver = requests.fullRequest.request_user_data.find(
                user => +user.traveler.user_id === +values.receiver_id
              )

              if (!receiver) {
                return
              }

              request_ticket_user_data.push(newUserData(receiver.traveler))
            }
          } else {
            request_ticket_user_data.push(...values.request_ticket_user_data)
          }
          dispatch(
            saveRequestTickets([
              {
                ...values,
                request_ticket_user_data
              }
            ])
          )
        }}
        validationSchema={getValidationSchema()}
        render={formikProps => (
          <Form onSubmit={formikProps.handleSubmit}>
            <FormFields
              formikProps={formikProps}
              refReceiverIdSelect={refReceiverIdSelect}
              request={requests.fullRequest}
            />
            {requests.saveFullRequestError && (
              <WrapAlert
                message={requests.saveFullRequestError}
                showIcon
                type="error"
              />
            )}
          </Form>
        )}
      />
    </Modal>
  )
}

export default ModalForm
